/**
 * Hook + helpers for per-certification access (paywall).
 * - useCertAccess(certCode, email) → { unlocked, loading, refresh }
 * - createCertCheckout({ cert_code, customer_email, customer_name }) → redirects to Stripe
 * - getCertStatus(sessionId) → polls the session
 */
import { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export async function createCertCheckout({ cert_code, customer_email, customer_name }) {
  const res = await fetch(`${API}/api/payments/cert-checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      cert_code,
      customer_email,
      customer_name,
      origin_url: window.location.origin,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Checkout failed: ${text}`);
  }
  return res.json();
}

export async function getCertStatus(sessionId) {
  const res = await fetch(`${API}/api/payments/cert-status/${sessionId}`);
  return res.json();
}

export async function getCertAccess(email) {
  if (!email) return { unlocked: [], purchases: [] };
  const res = await fetch(`${API}/api/payments/cert-access/${encodeURIComponent(email)}`);
  if (!res.ok) return { unlocked: [], purchases: [] };
  return res.json();
}

export function useCertAccess(certCode, email) {
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!email) {
      setUnlocked(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getCertAccess(email);
      setUnlocked((data.unlocked || []).includes(certCode));
    } finally {
      setLoading(false);
    }
  }, [certCode, email]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { unlocked, loading, refresh };
}
